import { HttpClient } from './http.js';
import { CheckoutModule } from './modules/checkout.js';
import { PaymentModule } from './modules/payment.js';
import { OffersModule } from './modules/offers.js';
import { CustomerModule } from './modules/customer.js';
import { CatalogModule } from './modules/catalog.js';
import { FunnelModule } from './modules/funnel.js';
import { RxModule } from './modules/rx.js';
import type { HeadlessConfig, Environment } from './types.js';

/**
 * Main entry point for the TagadaPay Headless SDK.
 *
 * Composes all domain modules (checkout, payment, offers, customer, catalog)
 * over a single lightweight HTTP client built on native fetch.
 *
 * **PCI DSS notice:** Card tokenization via `payment.tokenizeCard()` sends
 * card data directly to Basis Theory — it never reaches TagadaPay servers.
 * However, because card data passes through your JavaScript environment,
 * merchants using this SDK are responsible for PCI DSS SAQ-A-EP compliance.
 * See https://docs.tagada.io/developer-tools/headless-sdk/pci-compliance
 */
export class HeadlessClient {
  readonly config: Required<Pick<HeadlessConfig, 'storeId' | 'environment'>> & HeadlessConfig;

  private readonly http: HttpClient;

  /** Shared session token used by all modules for authenticated API calls. */
  private _sessionToken: string | undefined;

  /** Checkout session management -- load, update cart, apply promos, shipping. */
  readonly checkout: CheckoutModule;

  /** Payment processing -- setup config, APMs, tokenize, pay, 3DS, poll. */
  readonly payment: PaymentModule;

  /** Offers -- preview, pay, accept/decline upsells and order bumps. */
  readonly offers: OffersModule;

  /** Customer data -- profile, orders, subscriptions. */
  readonly customer: CustomerModule;

  /** Product catalog -- list products, variants, and prices. */
  readonly catalog: CatalogModule;

  /**
   * Funnel navigation -- bootstrap an anonymous CMS session and produce a
   * redirect URL into a funnel step using the currently loaded checkout cart.
   * Requires `config.accountId`.
   */
  readonly funnel: FunnelModule;

  /**
   * Tagada Rx -- clinical case submission and lookup for telehealth
   * storefronts. Active only on stores whose merchant has paid the
   * Tagada Rx setup fee and configured a clinical network.
   */
  readonly rx: RxModule;

  constructor(config: HeadlessConfig) {
    const environment: Environment = config.environment ?? 'production';
    const baseUrl = HttpClient.resolveBaseUrl(environment, config.baseUrl);

    this.config = {
      ...config,
      environment,
      baseUrl,
      timeout: config.timeout ?? 30_000,
    };

    this.http = new HttpClient({
      baseUrl,
      timeout: this.config.timeout!,
      apiKey: config.apiKey,
      getToken: () => this._sessionToken,
    });

    const setToken = (token: string) => {
      this._sessionToken = token;
    };

    this.checkout = new CheckoutModule(this.http, this.config.storeId, setToken);
    // The checkout module is handed to payments so express wallets can persist
    // the contact they collect in the sheet before charging (#TAG-1989).
    this.payment = new PaymentModule(this.http, this.config.storeId, environment, this.checkout);
    this.offers = new OffersModule(this.http, this.config.storeId, this.payment);
    this.customer = new CustomerModule(this.http, this.config.storeId);
    this.catalog = new CatalogModule(this.http, this.config.storeId);
    this.funnel = new FunnelModule(
      this.http,
      this.config.storeId,
      this.checkout,
      this.config.accountId,
    );
    this.rx = new RxModule(this.http);
  }

  /** Manually set the session token (e.g. from URL params on page load). */
  setSessionToken(token: string) {
    this._sessionToken = token;
  }
}

/**
 * Create a new HeadlessClient instance.
 *
 * @example
 * ```ts
 * import { createHeadlessClient } from '@tagadapay/headless-sdk';
 *
 * const tagada = createHeadlessClient({
 *   storeId: 'store_abc123',
 *   environment: 'production',
 * });
 *
 * const session = await tagada.checkout.loadSession(checkoutToken);
 * ```
 */
export function createHeadlessClient(config: HeadlessConfig): HeadlessClient {
  return new HeadlessClient(config);
}
